import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth, isCEO, isQC, isLocationUser, homeLocationOf } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { statusBadgeClass, STATUS_LABELS, SPEC_KEYS, fmtNum, formatApiError, LOCATIONS } from "@/lib/utils";
import { Filter, Layers, Trash2, Save, X, Pencil, FlaskConical, Search, Info, ArrowRightLeft, Blend, Truck } from "lucide-react";

const LOC_TABS = ["ALL", ...LOCATIONS];

// Stock Register has user-friendlier status names than the raw model.
// "Approved" feels confusing — show "AVAILABLE" once QC is done.
const STOCK_STATUS_LABELS = {
  ...STATUS_LABELS,
  approved: "Available",
  pending_qc: "Pending QC",
};

export default function Stock() {
  const { user } = useAuth();
  const isCEORole = isCEO(user);  // includes super_admin
  const isQCRole = isQC(user) || isCEORole;
  const lockedToLocation = isLocationUser(user);
  const home = homeLocationOf(user);

  const [batches, setBatches] = useState([]);
  const [statusFilter, setStatusFilter] = useState("approved");
  const [showTransit, setShowTransit] = useState(false); // toggle for the transit + discrepancies panel
  const [tab, setTab] = useState(lockedToLocation ? home : "ALL");
  const [expanded, setExpanded] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [labEdit, setLabEdit] = useState(null);
  const [editBatch, setEditBatch] = useState(null);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [trace, setTrace] = useState(null); // { batch, dispatches, transfers, blends }
  const [traceLoading, setTraceLoading] = useState(false);

  const openTrace = useCallback(async (b) => {
    setTraceLoading(true);
    setTrace({ batch: b, dispatches: [], transfers: [], blends: [], _loading: true });
    try {
      const r = await api.get(`/batches/${b.id}/traceability`);
      setTrace(r.data);
    } catch {
      setTrace({ batch: b, dispatches: [], transfers: [], blends: [], _error: true });
    } finally {
      setTraceLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    const r = await api.get("/batches");
    setBatches(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async (b) => {
    setErr("");
    try {
      await api.delete(`/batches/${b.id}`);
      setConfirm(null);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const openLabEdit = (b) => {
    const seed = SPEC_KEYS.reduce((acc, s) => ({ ...acc, [s.key]: b.lab_accurate?.[s.key] ?? "" }), {});
    setLabEdit({ batch: b, specs: seed, notes: b.lab_accurate_notes || "" });
  };
  const saveAccurateLab = async () => {
    setErr("");
    try {
      const numericSpecs = Object.fromEntries(
        Object.entries(labEdit.specs).map(([k, v]) => [k, v === "" ? null : parseFloat(v)])
      );
      await api.post(`/batches/${labEdit.batch.id}/lab-accurate`, { specs: numericSpecs, notes: labEdit.notes });
      setLabEdit(null);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const openEdit = (b) => {
    setEditBatch({
      id: b.id,
      batch_number: b.batch_number,
      production_date: b.production_date,
      shift: b.shift,
      bags_count: b.bags_count,
      location: b.location || "FACTORY",
      operator: b.operator || "",
      notes: b.notes || "",
    });
  };
  const saveEdit = async () => {
    setErr("");
    try {
      const payload = { ...editBatch };
      delete payload.id;
      payload.bags_count = parseInt(payload.bags_count, 10);
      await api.patch(`/batches/${editBatch.id}`, payload);
      setEditBatch(null);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const filteredAll = useMemo(() => {
    const term = search.trim().toLowerCase();
    return batches.filter((b) => {
      // In-transit + transferred rows are shown only in the separate "Transit + Discrepancies" panel
      if (b.status === "in_transit" || b.status === "transferred") return false;
      if (statusFilter !== "all" && b.status !== statusFilter) return false;
      if (!term) return true;
      const haystack = [
        b.batch_number,
        b.operator,
        b.notes,
        b.shortage_notes,
        b.shortage_bags ? `${b.shortage_bags} short` : "",
        b.transfer_from?.batch_number,
        b.transfer_from?.location,
        b.vehicle,
        b.location,
        b.created_by_name,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(term);
    });
  }, [batches, statusFilter, search]);

  // Separate list of transit + discrepancy rows shown only when the user opens the panel
  const transitAndDiscrepancy = useMemo(() => {
    return batches.filter((b) =>
      b.status === "in_transit" ||
      (b.status === "transferred" && (b.shortage_bags || 0) > 0) ||
      b.status === "transferred"
    );
  }, [batches]);
  const filtered = useMemo(
    () => filteredAll.filter((b) => tab === "ALL" ? true : (b.location || "FACTORY") === tab),
    [filteredAll, tab]
  );

  const tabCounts = useMemo(() => {
    const c = { ALL: filteredAll.length };
    for (const l of LOCATIONS) c[l] = filteredAll.filter((b) => (b.location || "FACTORY") === l).length;
    return c;
  }, [filteredAll]);

  const tabApprovedBags = useMemo(() => {
    const c = {};
    for (const t of LOC_TABS) {
      c[t] = filteredAll
        .filter((b) => (t === "ALL" || (b.location || "FACTORY") === t) && b.status === "approved")
        .reduce((s, b) => s + b.bags_remaining, 0);
    }
    return c;
  }, [filteredAll]);

  return (
    <div className="p-4 sm:p-8">
      <PageHeader
        overline="WAREHOUSE"
        title="Batch Stock Register"
        description="All production batches with status, location, lab reports and remaining bags. Tabs show stock per warehouse. CEO can edit any batch."
        actions={
          <div className="flex gap-2 items-center flex-wrap">
            <div className="flex items-center gap-1" style={{ minWidth: 200 }}>
              <Search size={14} color="var(--text-muted)" />
              <input
                type="text"
                placeholder="Search batch, source, operator, notes…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ padding: "6px 10px", fontSize: 13 }}
                data-testid="stock-search"
              />
            </div>
            <Filter size={14} color="var(--text-muted)" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: "auto" }} data-testid="stock-filter">
              <option value="approved">Available (QC done)</option>
              <option value="all">All Statuses</option>
              <option value="pending_qc">Pending QC</option>
              <option value="rejected">Rejected</option>
              <option value="mixed">Mixed</option>
              <option value="dispatched">Dispatched</option>
            </select>
          </div>
        }
      />

      {err && (
        <div className="card p-3 mb-4 text-xs" style={{ borderColor: "var(--red)", color: "var(--red)", background: "rgba(239,68,68,0.1)" }} data-testid="stock-error">
          {err}
        </div>
      )}

      {/* Location tabs (location users see only their warehouse) */}
      <div className="flex gap-1 mb-4 border-b overflow-x-auto" style={{ borderColor: "var(--border)" }}>
        {(lockedToLocation ? [home] : LOC_TABS).map((t) => (
          <button
            key={t}
            className="px-4 py-2 text-sm transition-colors whitespace-nowrap"
            style={{
              color: tab === t ? "var(--text)" : "var(--text-muted)",
              borderBottom: tab === t ? "2px solid var(--orange)" : "2px solid transparent",
              marginBottom: "-1px",
              fontWeight: tab === t ? 600 : 400,
            }}
            onClick={() => setTab(t)}
            data-testid={`stock-tab-${t.toLowerCase()}`}
          >
            {t} <span className="font-mono text-xs" style={{ color: "var(--text-dim)" }}>({tabCounts[t] || 0})</span>
          </button>
        ))}
      </div>

      {/* Always-visible per-location snapshot (cleanly broken out for CEO ask) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {(lockedToLocation ? [home] : LOC_TABS).map((t) => (
          <div key={t} className="card p-4" data-testid={`snapshot-${t.toLowerCase()}`}>
            <div className="stat-label">{t} {t === "ALL" && "(combined)"}</div>
            <div className="stat-value">{fmtNum(tabApprovedBags[t] || 0)}</div>
            <div className="stat-sub">available bags · {fmtNum((tabApprovedBags[t] || 0) * 50)} kg</div>
          </div>
        ))}
      </div>

      <div className="card overflow-hidden">
        <div style={{ overflowX: "auto" }}>
          <table className="table">
            <thead>
              <tr>
                <th>Batch No.</th>
                <th>Location</th>
                <th>Prod. Date</th>
                <th>Shift</th>
                <th className="text-right">Bags</th>
                <th className="text-right">Remaining</th>
                <th>Status</th>
                <th>Type</th>
                <th>Shortage</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="10" className="text-center py-8" style={{ color: "var(--text-muted)" }}>No batches.</td></tr>
              )}
              {filtered.map((b) => (
                <React.Fragment key={b.id}>
                  <tr data-testid={`stock-row-${b.batch_number}`}>
                    <td className="font-mono font-medium">{b.batch_number}</td>
                    <td className="font-mono text-xs uppercase">{b.location || "FACTORY"}</td>
                    <td className="font-mono text-xs">{b.production_date}</td>
                    <td className="font-mono uppercase text-xs">{b.shift}</td>
                    <td className="font-mono text-right">{fmtNum(b.bags_count)}</td>
                    <td className="font-mono text-right">{fmtNum(b.bags_remaining)}</td>
                    <td><span className={statusBadgeClass(b.status)}>{STOCK_STATUS_LABELS[b.status] || b.status}</span></td>
                    <td>
                      {b.is_mix ? (
                        <span className="badge badge-mixed"><Layers size={10} /> BLEND</span>
                      ) : (
                        <span style={{ color: "var(--text-dim)", fontSize: "11px" }}>—</span>
                      )}
                    </td>
                    <td>
                      {b.shortage_bags > 0 ? (
                        <div className="text-xs">
                          <div className="font-mono font-bold" style={{ color: "var(--red)" }}>
                            {b.shortage_bags} bags short
                          </div>
                          {b.shortage_notes && (
                            <div className="text-[10px]" style={{ color: "var(--text-muted)" }} title={b.shortage_notes}>
                              {b.shortage_notes.length > 30 ? b.shortage_notes.slice(0, 30) + "…" : b.shortage_notes}
                            </div>
                          )}
                          {b.transfer_from && (
                            <div className="text-[10px] font-mono" style={{ color: "var(--text-dim)" }}>
                              {b.transfer_from.batch_number} @ {b.transfer_from.location} → {b.location}
                            </div>
                          )}
                          {b.vehicle && (
                            <div className="text-[10px] font-mono" style={{ color: "var(--text-dim)" }}>
                              veh {b.vehicle}
                            </div>
                          )}
                          {b.unloaded_at && (
                            <div className="text-[10px] font-mono" style={{ color: "var(--text-dim)" }}>
                              unloaded {b.unloaded_at.slice(0, 10)}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: "var(--text-dim)", fontSize: "11px" }}>—</span>
                      )}
                    </td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        <button
                          className="btn btn-ghost"
                          style={{ padding: "4px 8px", fontSize: "11px" }}
                          onClick={() => openTrace(b)}
                          data-testid={`trace-batch-${b.batch_number}`}
                          title="View full traceability (dispatches, transfers, blends)"
                        >
                          <Info size={12} />
                        </button>
                        <button
                          className="btn btn-ghost"
                          style={{ padding: "4px 8px", fontSize: "11px" }}
                          onClick={() => setExpanded(expanded === b.id ? null : b.id)}
                          data-testid={`expand-${b.batch_number}`}
                        >
                          {expanded === b.id ? "Hide" : "Details"}
                        </button>
                        {isCEORole && (
                          <>
                            <button
                              className="btn btn-ghost"
                              style={{ padding: "4px 8px", fontSize: "11px", color: "var(--blue)" }}
                              onClick={() => openEdit(b)}
                              data-testid={`edit-batch-${b.batch_number}`}
                              title="Edit (CEO only)"
                            >
                              <Pencil size={12} />
                            </button>
                            <button
                              className="btn btn-ghost"
                              style={{ padding: "4px 8px", fontSize: "11px", color: "var(--red)" }}
                              onClick={() => { setConfirm(b); setErr(""); }}
                              data-testid={`delete-batch-${b.batch_number}`}
                              title="Delete (CEO only)"
                            >
                              <Trash2 size={12} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expanded === b.id && (
                    <tr style={{ background: "var(--bg)" }}>
                      <td colSpan="10" className="p-5">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                          <div>
                            <div className="text-xs font-mono mb-3" style={{ color: "var(--orange)" }}>// IN-HOUSE LAB</div>
                            {(b.lab_in_house || b.specs) ? (
                              <div className="grid grid-cols-2 gap-2">
                                {SPEC_KEYS.map((s) => {
                                  const src = b.lab_in_house || b.specs;
                                  const v = src?.[s.key];
                                  return (
                                    <div key={s.key} className="font-mono">
                                      <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>{s.label}</div>
                                      <div className="text-sm">{(v !== null && v !== undefined) ? `${v}${s.suffix || ""}` : "—"}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-sm" style={{ color: "var(--text-muted)" }}>QC not yet entered.</div>
                            )}
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <div className="text-xs font-mono" style={{ color: "var(--orange)" }}>// ACCURATE LAB</div>
                              {isQCRole && b.status !== "pending_qc" && (
                                <button className="btn btn-secondary" style={{ padding: "3px 8px", fontSize: "10px" }} onClick={() => openLabEdit(b)} data-testid={`edit-accurate-lab-${b.batch_number}`}>
                                  {b.lab_accurate ? "Edit" : "Add"}
                                </button>
                              )}
                            </div>
                            {b.lab_accurate ? (
                              <>
                                <div className="grid grid-cols-2 gap-2">
                                  {SPEC_KEYS.map((s) => (
                                    <div key={s.key} className="font-mono">
                                      <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>{s.label}</div>
                                      <div className="text-sm">
                                        {(b.lab_accurate[s.key] !== null && b.lab_accurate[s.key] !== undefined) ? `${b.lab_accurate[s.key]}${s.suffix || ""}` : "—"}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                {b.lab_accurate_by_name && (
                                  <div className="text-[10px] font-mono mt-3" style={{ color: "var(--text-dim)" }}>by {b.lab_accurate_by_name} · {b.lab_accurate_at?.slice(0, 10)}</div>
                                )}
                              </>
                            ) : (
                              <div className="text-sm" style={{ color: "var(--text-muted)" }}>External lab report not added yet.</div>
                            )}
                          </div>
                          <div>
                            <div className="text-xs font-mono mb-3" style={{ color: "var(--orange)" }}>// META</div>
                            <div className="space-y-1.5 text-xs font-mono" style={{ color: "var(--text-muted)" }}>
                              <div>Location: <span style={{ color: "var(--text)" }}>{b.location || "FACTORY"}</span></div>
                              <div>Created by: <span style={{ color: "var(--text)" }}>{b.created_by_name}</span></div>
                              {b.qc_by_name && <div>QC by: <span style={{ color: "var(--text)" }}>{b.qc_by_name}</span> on {b.qc_at?.slice(0, 10)}</div>}
                              {b.qc_notes && <div>QC Notes: <span style={{ color: "var(--text)" }}>{b.qc_notes}</span></div>}
                              {b.operator && <div>Operator: <span style={{ color: "var(--text)" }}>{b.operator}</span></div>}
                              {b.notes && <div>Notes: <span style={{ color: "var(--text)" }}>{b.notes}</span></div>}
                              {b.edited_by_name && <div>Edited by CEO: <span style={{ color: "var(--text)" }}>{b.edited_by_name}</span> on {b.edited_at?.slice(0,10)}</div>}
                              {b.transfer_from && (
                                <div className="pt-2 mt-2 border-t" style={{ borderColor: "var(--border)" }}>
                                  <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-dim)" }}>Transferred From</div>
                                  <div>{b.transfer_from.batch_number} @ {b.transfer_from.location}</div>
                                </div>
                              )}
                              {b.is_mix && b.source_batches?.length > 0 && (
                                <div className="pt-2 mt-2 border-t" style={{ borderColor: "var(--border)" }}>
                                  <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-dim)" }}>Blend Sources</div>
                                  {b.source_batches.map((s) => (
                                    <div key={s.batch_id}>· {s.batch_number} — {s.bags_used} bags ({s.production_date})</div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirm */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => setConfirm(null)}>
          <div className="card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="confirm-delete-modal">
            <div className="text-xs font-mono mb-2" style={{ color: "var(--red)" }}>// CEO OVERRIDE — DELETE BATCH</div>
            <h3 className="font-heading text-xl font-bold mb-3">Delete this batch?</h3>
            <div className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
              You are about to permanently delete batch{" "}
              <span className="font-mono" style={{ color: "var(--text)" }}>{confirm.batch_number}</span>{" "}
              ({confirm.bags_count} bags, status: {STOCK_STATUS_LABELS[confirm.status] || confirm.status}). It will move to <strong>Recently Deleted</strong> and stay recoverable for 60 days.
            </div>
            <div className="text-xs p-2 mb-4" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid var(--red)", color: "var(--red)" }}>
              Note: batches that are active sources of a blend cannot be deleted — delete the blend first.
            </div>
            <div className="flex gap-3">
              <button className="btn btn-secondary flex-1" onClick={() => setConfirm(null)} data-testid="cancel-delete-button">Cancel</button>
              <button className="btn btn-danger flex-1" onClick={() => handleDelete(confirm)} data-testid="confirm-delete-button">
                <Trash2 size={14} /> Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CEO Edit modal */}
      {editBatch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => setEditBatch(null)}>
          <div className="card p-6 max-w-xl w-full" onClick={(e) => e.stopPropagation()} data-testid="edit-batch-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs font-mono mb-1" style={{ color: "var(--blue)" }}>// CEO EDIT — BATCH METADATA</div>
                <div className="font-heading text-xl font-bold">Edit Batch</div>
              </div>
              <button className="btn btn-ghost" style={{ padding: 4 }} onClick={() => setEditBatch(null)}><X size={16} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label>Batch Number</label>
                <input value={editBatch.batch_number} onChange={(e) => setEditBatch({ ...editBatch, batch_number: e.target.value })} data-testid="edit-batch-number" />
              </div>
              <div>
                <label>Production Date</label>
                <input type="date" value={editBatch.production_date} onChange={(e) => setEditBatch({ ...editBatch, production_date: e.target.value })} data-testid="edit-prod-date" />
              </div>
              <div>
                <label>Shift</label>
                <select value={editBatch.shift} onChange={(e) => setEditBatch({ ...editBatch, shift: e.target.value })} data-testid="edit-shift">
                  <option value="day">Day</option>
                  <option value="night">Night</option>
                </select>
              </div>
              <div>
                <label>Location</label>
                <select value={editBatch.location} onChange={(e) => setEditBatch({ ...editBatch, location: e.target.value })} data-testid="edit-location">
                  {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label>Bags Count</label>
                <input type="number" value={editBatch.bags_count} onChange={(e) => setEditBatch({ ...editBatch, bags_count: e.target.value })} data-testid="edit-bags" />
              </div>
              <div>
                <label>Operator</label>
                <input value={editBatch.operator} onChange={(e) => setEditBatch({ ...editBatch, operator: e.target.value })} data-testid="edit-operator" />
              </div>
            </div>
            <label>Notes</label>
            <textarea rows="2" value={editBatch.notes} onChange={(e) => setEditBatch({ ...editBatch, notes: e.target.value })} data-testid="edit-notes" />
            <div className="text-xs p-2 mt-3 mb-4" style={{ background: "rgba(59,130,246,0.1)", border: "1px solid var(--blue)", color: "var(--blue)" }}>
              Status and lab reports are not editable here. Bags-count cannot be set below already-consumed bags.
            </div>
            <div className="flex gap-3">
              <button className="btn btn-secondary flex-1" onClick={() => setEditBatch(null)}>Cancel</button>
              <button className="btn btn-primary flex-1" onClick={saveEdit} data-testid="save-edit-button">
                <Save size={14} /> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Accurate Lab editor */}
      {labEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => setLabEdit(null)}>
          <div className="card p-6 max-w-2xl w-full" onClick={(e) => e.stopPropagation()} data-testid="accurate-lab-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs font-mono mb-1" style={{ color: "var(--orange)" }}>// ACCURATE LAB REPORT</div>
                <div className="font-heading text-xl font-bold tracking-tight font-mono">{labEdit.batch.batch_number}</div>
              </div>
              <button className="btn btn-ghost" style={{ padding: 4 }} onClick={() => setLabEdit(null)}><X size={16} /></button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {SPEC_KEYS.map((s) => (
                <div key={s.key}>
                  <label style={{ fontSize: 10 }}>{s.label}{s.suffix && ` (${s.suffix.trim()})`}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={labEdit.specs[s.key]}
                    onChange={(e) => setLabEdit({ ...labEdit, specs: { ...labEdit.specs, [s.key]: e.target.value } })}
                    placeholder="—"
                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                    data-testid={`accurate-spec-${s.key}-input`}
                  />
                </div>
              ))}
            </div>
            <label>Notes (lab name, certificate ref, etc.)</label>
            <textarea rows="2" value={labEdit.notes} onChange={(e) => setLabEdit({ ...labEdit, notes: e.target.value })} data-testid="accurate-lab-notes-input" />
            <div className="flex gap-3 mt-4">
              <button className="btn btn-secondary flex-1" onClick={() => setLabEdit(null)}>Cancel</button>
              <button className="btn btn-primary flex-1" onClick={saveAccurateLab} data-testid="save-accurate-lab-button">
                <Save size={14} /> Save Accurate Lab
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transit + Discrepancies — hidden by default. Shows in-transit consignments and
          any transferred consignments that had a shortage or additional bags. */}
      <div className="card mt-4">
        <button
          className="w-full flex items-center justify-between px-4 py-3 text-left"
          onClick={() => setShowTransit((v) => !v)}
          data-testid="toggle-transit-panel"
          style={{ background: "transparent", border: "none", cursor: "pointer" }}
        >
          <div className="font-heading text-sm font-semibold flex items-center gap-2">
            <Truck size={14} color="var(--brand-blue)" />
            TRANSIT &amp; DISCREPANCIES
            <span className="font-mono text-xs" style={{ color: "var(--text-dim)" }}>({transitAndDiscrepancy.length})</span>
          </div>
          <span className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
            {showTransit ? "hide" : "show"}
          </span>
        </button>
        {showTransit && (
          <div style={{ overflowX: "auto", borderTop: "1px solid var(--border)" }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Lot / Batch No.</th>
                  <th>Route</th>
                  <th>Vehicle</th>
                  <th className="text-right">Bags</th>
                  <th>Status</th>
                  <th>Discrepancy</th>
                  <th>Sent / Unloaded</th>
                </tr>
              </thead>
              <tbody>
                {transitAndDiscrepancy.length === 0 && (
                  <tr><td colSpan="7" className="text-center py-6" style={{ color: "var(--text-muted)" }}>Nothing in transit and no discrepancies.</td></tr>
                )}
                {transitAndDiscrepancy.map((b) => {
                  // Prefer the destination lot names (from lot children created during unload) as identity.
                  // Fall back to source batch → never show the synthetic TRANSIT-xxx-hash id.
                  const identity = b.transfer_from?.batch_number || b.batch_number;
                  const shortBags = b.shortage_bags || 0;
                  const discrepancyType = b.discrepancy_type || (shortBags > 0 ? "short" : "");
                  return (
                    <tr key={b.id} data-testid={`transit-row-${identity}`}>
                      <td className="font-mono">
                        <div className="font-medium">{identity}</div>
                        {b.transfer_from?.batch_number && b.batch_number !== identity && (
                          <div className="text-[10px]" style={{ color: "var(--text-dim)" }}>
                            (transit ref)
                          </div>
                        )}
                      </td>
                      <td className="font-mono text-xs">
                        {b.transfer_from?.location || "—"} → {b.location}
                      </td>
                      <td className="font-mono text-xs">{b.vehicle || "—"}</td>
                      <td className="font-mono text-right">
                        {fmtNum(b.bags_remaining ?? b.bags_count)}
                        {b.bags_count !== (b.bags_remaining ?? b.bags_count) && (
                          <span className="block text-[10px]" style={{ color: "var(--text-dim)" }}>of {fmtNum(b.bags_count)}</span>
                        )}
                      </td>
                      <td>
                        <span className={statusBadgeClass(b.status)}>
                          {b.status === "in_transit" ? "IN TRANSIT" : "TRANSFERRED"}
                        </span>
                      </td>
                      <td>
                        {shortBags > 0 ? (
                          <div className="text-xs">
                            <div className="font-mono font-bold" style={{ color: discrepancyType === "additional" ? "var(--green)" : "var(--red)" }}>
                              {shortBags} bags {discrepancyType === "additional" ? "additional" : "short"}
                            </div>
                            {b.shortage_notes && (
                              <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>{b.shortage_notes}</div>
                            )}
                          </div>
                        ) : <span style={{ color: "var(--text-dim)", fontSize: 11 }}>—</span>}
                      </td>
                      <td className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
                        {b.status === "in_transit"
                          ? `sent ${(b.created_at || "").slice(0, 10)}`
                          : `unloaded ${(b.unloaded_at || b.created_at || "").slice(0, 10)}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Full batch traceability drawer (dispatches + transfers + blends) */}
      {trace && (
        <div className="fixed inset-0 z-50 flex" style={{ background: "rgba(13,42,78,0.45)" }} onClick={() => setTrace(null)}>
          <div className="ml-auto h-full w-full max-w-2xl overflow-auto" style={{ background: "var(--surface)", boxShadow: "var(--shadow-lg)" }} onClick={(e) => e.stopPropagation()} data-testid="batch-trace-drawer">
            <div className="sticky top-0 z-10 px-6 py-4 border-b flex items-center justify-between" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div>
                <div className="text-xs font-mono" style={{ color: "var(--brand-blue)" }}>// BATCH TRACEABILITY</div>
                <h3 className="font-heading text-xl font-bold mt-1 font-mono">{trace.batch?.batch_number}</h3>
              </div>
              <button className="btn btn-ghost" style={{ padding: 4 }} onClick={() => setTrace(null)} data-testid="close-trace"><X size={18} /></button>
            </div>

            <div className="p-6 space-y-5">
              {/* Batch summary */}
              <div className="card p-4">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs font-mono">
                  <div><span style={{ color: "var(--text-muted)" }}>Status:</span> <span className={statusBadgeClass(trace.batch?.status)}>{STOCK_STATUS_LABELS[trace.batch?.status] || trace.batch?.status}</span></div>
                  <div><span style={{ color: "var(--text-muted)" }}>Location:</span> {trace.batch?.location}</div>
                  <div><span style={{ color: "var(--text-muted)" }}>Date of Mfg:</span> {trace.batch?.production_date || "—"}</div>
                  <div><span style={{ color: "var(--text-muted)" }}>Shift:</span> {trace.batch?.shift?.toUpperCase() || "—"}</div>
                  <div><span style={{ color: "var(--text-muted)" }}>Bags total / left:</span> {fmtNum(trace.batch?.bags_count)} / {fmtNum(trace.batch?.bags_remaining || 0)}</div>
                  <div><span style={{ color: "var(--text-muted)" }}>Operator:</span> {trace.batch?.operator || "—"}</div>
                  {trace.batch?.qc_at && (
                    <div className="col-span-2 pt-1.5 mt-1.5" style={{ borderTop: "1px dashed var(--border)" }}>
                      <span style={{ color: "var(--brand-blue)" }}>QC:</span>{" "}
                      <span style={{ color: "var(--green)" }}>Approved</span> on {trace.batch.qc_at.slice(0, 16).replace("T", " ")} by {trace.batch.qc_by_name || "?"}
                    </div>
                  )}
                  {trace.batch?.transfer_from && (
                    <div className="col-span-2 pt-1.5 mt-1.5" style={{ borderTop: "1px dashed var(--border)" }}>
                      <ArrowRightLeft size={11} style={{ display: "inline", marginRight: 4, color: "var(--brand-blue)" }} />
                      <span style={{ color: "var(--brand-blue)" }}>Transferred in from:</span>{" "}
                      <strong>{trace.batch.transfer_from.batch_number}</strong> @ {trace.batch.transfer_from.location}
                    </div>
                  )}
                  {trace.batch?.is_mix && trace.batch?.source_batches && trace.batch.source_batches.length > 0 && (
                    <div className="col-span-2 pt-1.5 mt-1.5" style={{ borderTop: "1px dashed var(--border)" }}>
                      <Blend size={11} style={{ display: "inline", marginRight: 4, color: "var(--brand-blue)" }} />
                      <span style={{ color: "var(--brand-blue)" }}>Blended from:</span>{" "}
                      {trace.batch.source_batches.map((sb, k) => (
                        <span key={sb.batch_id || sb.batch_number || k}>{k > 0 ? ", " : ""}<strong>{sb.batch_number}</strong> ({sb.bags_taken || sb.bags || "?"}b)</span>
                      ))}
                    </div>
                  )}
                  {trace.batch?.specs && (
                    <div className="col-span-2 pt-1.5 mt-1.5" style={{ borderTop: "1px dashed var(--border)" }}>
                      <FlaskConical size={11} style={{ display: "inline", marginRight: 4, color: "var(--brand-blue)" }} />
                      <span style={{ color: "var(--brand-blue)" }}>Specs:</span>{" "}
                      {[
                        ["Prot", trace.batch.specs.protein, "%"],
                        ["Moist", trace.batch.specs.moisture, "%"],
                        ["S/Si", trace.batch.specs.sand_silica, "%"],
                        ["Salt", trace.batch.specs.salt, "%"],
                        ["Fat", trace.batch.specs.fat, "%"],
                        ["TVN", trace.batch.specs.volatile_nitrogen, "mg"],
                      ].filter(([_, v]) => v !== null && v !== undefined).map(([k, v, unit], n) => (
                        <span key={k}>{n > 0 ? " · " : ""}{k} <strong>{v}{unit}</strong></span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Dispatches that consumed this batch */}
              <div>
                <div className="text-xs font-mono mb-2" style={{ color: "var(--brand-blue)" }}>
                  // DISPATCHES ({trace.dispatches?.length || 0})
                </div>
                {(!trace.dispatches || trace.dispatches.length === 0) ? (
                  <div className="text-xs" style={{ color: "var(--text-muted)" }}>No dispatches yet.</div>
                ) : (
                  <div className="space-y-2">
                    {trace.dispatches.map((d) => (
                      <div key={d.id} className="card p-3" data-testid={`trace-dispatch-${d.id}`} style={{ opacity: d._deleted ? 0.6 : 1 }}>
                        <div className="flex justify-between items-start mb-1">
                          <div className="font-mono text-sm">
                            <span className={`badge ${d.kind === "export" ? "badge-mixed" : "badge-transferred"} mr-2`} style={{ fontSize: 9 }}>
                              {d.kind === "export" ? "EXPORT" : "DOMESTIC"}
                            </span>
                            {d.customer}
                          </div>
                          <div className="font-mono text-xs text-right" style={{ color: "var(--text-muted)" }}>
                            {fmtNum(d.bags)} bags
                            {d._deleted && <span className="block text-[10px]" style={{ color: "var(--red)" }}>DELETED</span>}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs font-mono" style={{ color: "var(--text-muted)" }}>
                          <div>Date: {d.created_at?.slice(0, 16).replace("T", " ")}</div>
                          <div>By: {d.created_by_name || "—"}</div>
                          <div>Vehicle: {d.vehicle || "—"}</div>
                          <div>Invoice: {d.invoice_number || "—"}</div>
                          <div>Dispatch Batch: {d.dispatch_batch_number || "—"}</div>
                          {d.kind === "export" && <div>Container: {d.container_number || "—"} {d.seal_number ? `· seal ${d.seal_number}` : ""}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Transfers involving this batch as source */}
              <div>
                <div className="text-xs font-mono mb-2" style={{ color: "var(--brand-blue)" }}>
                  // TRANSFERS ({trace.transfers?.length || 0})
                </div>
                {(!trace.transfers || trace.transfers.length === 0) ? (
                  <div className="text-xs" style={{ color: "var(--text-muted)" }}>Not used as a transfer source.</div>
                ) : (
                  <div className="space-y-2">
                    {trace.transfers.map((t) => (
                      <div key={t.id} className="card p-3" data-testid={`trace-transfer-${t.id}`} style={{ opacity: t._deleted ? 0.6 : 1 }}>
                        <div className="flex justify-between items-center">
                          <div className="font-mono text-sm">
                            <ArrowRightLeft size={12} style={{ display: "inline", marginRight: 4, color: "var(--brand-blue)" }} />
                            {t.from_location} → {t.to_location}
                          </div>
                          <div className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
                            {fmtNum(t.bags)} bags
                          </div>
                        </div>
                        <div className="text-xs font-mono mt-1" style={{ color: "var(--text-muted)" }}>
                          {t.created_at?.slice(0, 16).replace("T", " ")} · veh {t.vehicle || "—"} · by {t.created_by_name || "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Blends this batch was a source of */}
              {trace.blends && trace.blends.length > 0 && (
                <div>
                  <div className="text-xs font-mono mb-2" style={{ color: "var(--brand-blue)" }}>
                    // USED IN BLENDS ({trace.blends.length})
                  </div>
                  <div className="space-y-2">
                    {trace.blends.map((bl) => (
                      <div key={bl.id} className="card p-3" data-testid={`trace-blend-${bl.id}`} style={{ opacity: bl._deleted ? 0.6 : 1 }}>
                        <div className="flex justify-between items-center">
                          <div className="font-mono text-sm">
                            <Blend size={12} style={{ display: "inline", marginRight: 4, color: "var(--brand-blue)" }} />
                            {bl.batch_number}
                          </div>
                          <div className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
                            {fmtNum(bl.bags_taken)} bags taken
                          </div>
                        </div>
                        <div className="text-xs font-mono mt-1" style={{ color: "var(--text-muted)" }}>
                          {bl.production_date} · @ {bl.location}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
